// LoanProducts.js
import React, { useState } from "react";
// import { useGetActiveLoanProductsQuery } from "../../services/loanApiSlice";
import { useGetActiveLoanProductsQuery } from "../../services/loanProductApiSlice";
import {
  CurrencyDollarIcon,
  CalendarIcon,
  TagIcon,
  ScaleIcon,
  MagnifyingGlassIcon,
  FolderIcon, // Unique header icon
} from "@heroicons/react/24/outline";

const LoanProducts = () => {
  const { data: products, isLoading, isError, error } = useGetActiveLoanProductsQuery();
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate stats (based on original products)
  const totalProducts = products?.length || 0;
  const avgInterest = products?.length
    ? (products.reduce((sum, p) => sum + p.interestRate, 0) / products.length).toFixed(2)
    : 0;
  const avgTerm = products?.length
    ? (products.reduce((sum, p) => sum + p.defaultTermMonths, 0) / products.length).toFixed(0)
    : 0;
  const totalLateFee = products?.reduce((sum, p) => sum + p.lateFeeFixedAmount, 0) || 0;

  // Filter products based on search term
  const filteredProducts = products?.filter((product) =>
    product.loanProductName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.loanProductId.toString().includes(searchTerm)
  ) || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-red-600 p-4 bg-red-50 rounded-lg">
        Error loading loan products: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header Section */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <FolderIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Loan Products</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and view all active loan products</p>
          </div>
        </div>
        {/* Search Bar */}
        <div className="mt-4 sm:mt-0 relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
          />
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Glassmorphic Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg rounded-xl p-6 flex items-center hover:shadow-xl transition-shadow duration-200">
          <div className="rounded-full bg-blue-100 p-3 mr-4">
            <TagIcon className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Products</p>
            <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg rounded-xl p-6 flex items-center hover:shadow-xl transition-shadow duration-200">
          <div className="rounded-full bg-blue-100 p-3 mr-4">
            <CurrencyDollarIcon className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Avg Interest Rate</p>
            <p className="text-2xl font-bold text-gray-900">{avgInterest}%</p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg rounded-xl p-6 flex items-center hover:shadow-xl transition-shadow duration-200">
          <div className="rounded-full bg-blue-100 p-3 mr-4">
            <CalendarIcon className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Avg Term (months)</p>
            <p className="text-2xl font-bold text-gray-900">{avgTerm}</p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg rounded-xl p-6 flex items-center hover:shadow-xl transition-shadow duration-200">
          <div className="rounded-full bg-blue-100 p-3 mr-4">
            <ScaleIcon className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Late Fees (LKR)</p>
            <p className="text-2xl font-bold text-gray-900">Rs {totalLateFee.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-800">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Product ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Interest Rate (%)
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Default Term (months)
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Late Fee Fixed (LKR)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => (
                  <tr
                    key={product.loanProductId}
                    className={`hover:bg-blue-50 transition-colors duration-150 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.loanProductId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.loanProductName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.interestRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.defaultTermMonths}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      LKR {product.lateFeeFixedAmount}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-10 text-center text-sm text-gray-500 bg-gray-50"
                  >
                    No active loan products match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LoanProducts;